import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter, HostBinding,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { Keys } from '../../../_core/utils/keys';
import { PopoverComponent } from '../popover/popover.component';

@Component({
  selector: 'opac-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements AfterViewInit {
  isSubmenu = false;

  /**
   * Emits when any option is selected
   */
  @Output() selected = new EventEmitter<void>();

  /**
   * Saves ul element to add it some listeners in `menu-trigger.directive.ts`
   */
  @ViewChild('element') element!: ElementRef<HTMLElement>;
  /**
   * Saves popover component to send it to `menu-trigger.directive.ts` parent.
   */
  @ViewChild('popover', { static: true }) popover!: PopoverComponent;
  /**
   * Gets all `menu-item.component`s inside this menu.
   */
  @ContentChildren(MenuItemComponent) items!: QueryList<MenuItemComponent>;

  ngAfterViewInit(): void {
    this.items.changes.subscribe(() => {
      if (this.items.length > 0) {
        this.setCurrentItem(this.items.get(0));
      }
    });
  }

  handleClick(event: MouseEvent) {
    this.selected.emit();
  }

  handleKeydown(event: KeyboardEvent) {
    if (!this.popover.isOpen) {
      return;
    }

    const activeItem = this.getCurrentItem();

    if ([Keys.Enter, Keys.Space].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();

      activeItem?.click();
      return;
    }

    // Move through options with keyboard
    if ([Keys.ArrowDown, Keys.ArrowUp, Keys.End, Keys.Home].includes(event.key)) {
      let index = activeItem ? this.items.toArray().indexOf(activeItem) : 0;

      if (this.items.length === 0) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      switch (event.key) {
        case Keys.ArrowDown:
          index++;
          break;
        case Keys.ArrowUp:
          index--;
          break;
        case Keys.Home:
          index = 0;
          break;
        case Keys.End:
          index = this.items.length - 1;
          break;
      }

      if (index < 0) {
        index = this.items.length - 1;
      }

      if (index > this.items.length - 1) {
        index = 0;
      }

      const selectedItem = this.items.get(index);

      if (selectedItem) {
        this.setCurrentItem(selectedItem);
        selectedItem.focus();
      }
    }
  }

  handleMousedown(event: Event) {
    const target = event.target as HTMLElement;
    const targetComponent = this.getMenuItemById(target.id);

    if (targetComponent) {
      this.setCurrentItem(targetComponent);
    }
  }

  /**
   * @internal
   * Get current selected menu item
   */
  getCurrentItem(): MenuItemComponent | undefined {
    return this.items.find((menuItem: MenuItemComponent) => menuItem.tabindex === 0);
  }

  /**
   * @internal
   * Set current selected menu item
   */
  setCurrentItem(item: MenuItemComponent) {
    this.items.forEach((menuItem: MenuItemComponent) => {
      menuItem.tabindex = menuItem === item ? 0 : -1;
    });
  }

  /**
   * @internal
   * Get any menu item by its id attr.
   */
  getMenuItemById(id: string): MenuItemComponent | undefined {
    return this.items.find((menuItem: MenuItemComponent) => menuItem.id === id);
  }
}
